import { Box, Typography } from "@mui/material";

import * as Sentry from "@sentry/react";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../../components/common/BriaButton/BriaButton";
import BriaDropDown from "../../../../../../components/common/BriaDropDown/BriaDropDown";
import BriaInput from "../../../../../../components/common/BriaInput/BriaInput";
import useStaticDropdown from "../../../../../../components/common/DropDowns/useStaticDropdown";
import { APPS, IMAGES_RESOLUTIONS_LIST } from "../../../../../../constants/AppsConstants";
import { ImageEditingFeatureDetector } from "../../../../../../constants/ImageEditingFeatures";
import LinkConstants from "../../../../../../constants/LinkConstants";
import { POSTHOG_EVENTS } from "../../../../../../constants/PostHogEvents";
import { IMAGE_EDITING_FEATURES } from "../../../../../../constants/routesMapper";
import { useLogEvents } from "../../../../../../hooks/useLogEvents";
import { useManualPlacementConfig } from "../../../../../../hooks/useManualPlacementConfig";
import usePostHog from "../../../../../../hooks/usePostHog";
import { useAppStore } from "../../../../../../hooks/useStores";
import InputLayout from "../../../../../../layout/InputLayout/InputLayout";
import {
	ImageEditingConfigComponentProps,
	ImageEditingViewMode,
	Platform,
	PlaygroundImage,
	ResponsePlatformDestination,
} from "../../../../../../models/image-to-image";
import { hasOrganization, isPlaygroundLoadingImages } from "../../../../../../utils";
import Config from "../../Config/Config";
import styles from "./ManualPlacement.module.scss";

const ManualPlacementConfig: React.FC<ImageEditingConfigComponentProps> = ({ onBackButtonClick }) => {
	const { t } = useTranslation("translation", {
		keyPrefix: "playground.imageToImage.config.features.manualPlacement",
	});
	const { items: AspectRatiosList } = useStaticDropdown({ tKeyPrefix: "staticDropdowns.aspectRatio" });
	const { imageToImageStore, playgroundStore } = useAppStore();
	const { trackEvent } = usePostHog();
	const {
		selectedConfig,
		setSelectedConfig,
		selectedAspectRatio,
		selectedPlatform,
		selectedPlatformDimensions,
		cropConfig,
		platformsList,
		customWidth,
		setCustomWidth,
		customHeight,
		setCustomHeight,
		handleConfigChange,
		handleMediumDestinationChange,
		handleAspectRatioSelection,
		handlePlatformSelection,
		setSelectedAspectRatio,
		setSelectedPlatformDimensions,
		checkResolutions,
		exceededResolutions,
		hasNoTransparentArea,
		calculateImageDimensions,
		getCropConfig,
	} = useManualPlacementConfig();

	const [selectedImage, setSelectedImage] = useState<PlaygroundImage | null>();
	const { logExpandEvent, logCropEvent, logIncreaseResolutionEvent } = useLogEvents();
	const hasTextToImageLoadingImages =
		isPlaygroundLoadingImages(playgroundStore.playgroundResults, APPS.TEXT_TO_IMAGE) || false;

	useEffect(() => {
		imageToImageStore.manualPlacementStore.toggleManualPlacementConfig(true);
		getCropConfig();
		return () => {
			imageToImageStore.manualPlacementStore.toggleManualPlacementConfig(false);
		};
	}, [getCropConfig, imageToImageStore]);

	useEffect(() => {
		setOriginalAspectRatioDefault();
	}, []);

	useEffect(() => {
		if (playgroundStore.imageEditingViewMode !== ImageEditingViewMode.SINGLE) {
			onBackButtonClick?.();
		}
		checkResolutions();
	}, [playgroundStore.imageEditingViewMode, onBackButtonClick]);

	useEffect(() => {
		playgroundStore.playgroundResults.length > 0
			? setSelectedImage(playgroundStore.singleSelectedImage || playgroundStore.getSelectedImages()[0])
			: setSelectedImage(null);
		calculateImageDimensions();
	}, [playgroundStore.playgroundResults, playgroundStore.singleSelectedImage]);

	const setOriginalAspectRatioDefault = () => {
		setSelectedConfig(t("aspectRatio"));
		handleAspectRatioSelection("original");
		imageToImageStore.handleConfigChange("background", {
			...imageToImageStore.config.background,
			expand: false,
			crop: false,
		});
	};

	const handleExpandBackground = async (keep_aspect_ratio_value_in_config: boolean) => {
		const selectedImage =
			playgroundStore.singleSelectedImage || playgroundStore.getAvailableImages().find((image) => image.selected);
		if (selectedImage) {
			const startTime = Date.now();
			const featureNames = ImageEditingFeatureDetector.getFeatureDisplayNames();
			const expandFeature = featureNames[IMAGE_EDITING_FEATURES.EXPAND_BACKGROUND];

			// Common event properties for this action
			const commonEventProps = {
				feature: expandFeature,
				mode: ImageEditingViewMode.SINGLE,
			};

			// Track manual placement expand started
			trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_STARTED, commonEventProps);

			try {
				imageToImageStore.handleConfigChange("background", {
					...imageToImageStore.config.background,
					expand: true,
				});
				await imageToImageStore.expandBackground(selectedImage, undefined, keep_aspect_ratio_value_in_config);

				// Track successful expand
				const processingTime = Date.now() - startTime;
				trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_COMPLETED, {
					...commonEventProps,
					processing_time: processingTime,
					send_instantly: true,
				});

				logExpandEvent();
				setOriginalAspectRatioDefault();
			} catch (e) {
				// Track failed expand
				trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_FAILED, {
					...commonEventProps,
					error: e instanceof Error ? e.message : String(e),
					send_instantly: true,
				});

				Sentry.captureMessage(`Error expanding object: ${e}`);
				console.error(`Error expanding object: ${e}`);
			}
		}
	};

	const handleCropImage = async () => {
		if (selectedImage) {
			const startTime = Date.now();
			const featureNames = ImageEditingFeatureDetector.getFeatureDisplayNames();
			const cropFeature = featureNames[IMAGE_EDITING_FEATURES.CROP_IMAGE];

			// Common event properties for this action
			const commonEventProps = {
				feature: cropFeature,
				mode: ImageEditingViewMode.SINGLE,
			};

			// Track manual placement crop started
			trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_STARTED, commonEventProps);

			try {
				imageToImageStore.handleConfigChange("background", {
					...imageToImageStore.config.background,
					crop: true,
				});
				await imageToImageStore.cropImage(selectedImage);

				// Track successful crop
				const processingTime = Date.now() - startTime;
				trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_COMPLETED, {
					...commonEventProps,
					processing_time: processingTime,
					send_instantly: true,
				});

				logCropEvent();
				setOriginalAspectRatioDefault();
				resetOtherConfigs("customSize");
			} catch (e) {
				// Track failed crop
				trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_FAILED, {
					...commonEventProps,
					error: e instanceof Error ? e.message : String(e),
					send_instantly: true,
				});

				Sentry.captureMessage(`Error cropping the image: ${e}`);
				console.error("Error cropping the image:", e);
			}
		}
	};

	const resetOtherConfigs = (currentConfig: string) => {
		if (currentConfig !== t("aspectRatio")) {
			setSelectedAspectRatio("");
		}
		if (currentConfig !== t("customSize")) {
			setCustomHeight("");
			setCustomWidth("");
		}
		if (currentConfig !== t("mediumDestination")) {
			setSelectedPlatformDimensions({});
		}
	};

	const renderAspectRatioConfig = () => (
		<Config
			title={t("aspectRatio")}
			checked={selectedConfig === t("aspectRatio")}
			onClick={() => {
				setSelectedConfig(t("aspectRatio"));
				resetOtherConfigs(t("aspectRatio"));
				handleAspectRatioSelection(selectedAspectRatio);
			}}
			isActive={selectedConfig == t("aspectRatio")}
			setIsActive={setSelectedConfig}
			apiDocLink={LinkConstants.BRIA_API_IMAGE_EXPANSTION}
			titleOnly
		>
			<Box className={styles.configContent}>
				<Box className={styles.aspectRatioList}>
					{AspectRatiosList?.map((aspectRatio, index) => {
						const value = aspectRatio.value as string;
						const isSelected = value === selectedAspectRatio;
						return (
							<BriaButton
								key={index}
								onClick={() => {
									setSelectedConfig(t("aspectRatio"));
									resetOtherConfigs(t("aspectRatio"));
									handleAspectRatioSelection(value);
									imageToImageStore.handleConfigChange("size", {
										aspect_ratio: {
											value: [value],
											selected: true,
										},
									});
								}}
								className={clsx(styles.aspectRatioBtn, { [styles.selected]: isSelected })}
								sx={{ aspectRatio: value.replace(":", "/") }}
							>
								{value}
							</BriaButton>
						);
					})}
				</Box>
				{renderActions(true)}
			</Box>
		</Config>
	);

	const renderCustomSizeConfig = () => (
		<Config
			title={t("customSize")}
			checked={selectedConfig === t("customSize")}
			onClick={() => {
				setSelectedConfig(t("customSize"));
				resetOtherConfigs(t("customSize"));
				if (customWidth && customHeight) {
					handleConfigChange("width", parseInt(customWidth));
					handleConfigChange("height", parseInt(customHeight));
				}
			}}
			isActive={selectedConfig == t("customSize")}
			setIsActive={setSelectedConfig}
			titleOnly
			apiDocLink={LinkConstants.BRIA_API_IMAGE_EXPANSTION}
		>
			<Box className={clsx(styles.configContent)}>
				<Box className={styles.dimensions}>
					<InputLayout label={t("width")} className={styles.inputLayout}>
						<BriaInput
							value={customWidth}
							onChange={(e) => {
								const target = e.target as HTMLInputElement;
								setCustomWidth(target.value);
								const width = parseInt(target.value);
								handleConfigChange(
									"width",
									isNaN(width)
										? imageToImageStore.config.manual_placement?.original_image_size?.[0]!
										: width,
								);
								if (customHeight) {
									resetOtherConfigs(t("customSize"));
								}
							}}
							type="number"
							customEndAdornmentText="px"
							height="40px"
						/>
					</InputLayout>

					<InputLayout label={t("height")} className={styles.inputLayout}>
						<BriaInput
							value={customHeight}
							onChange={(e) => {
								const target = e.target as HTMLInputElement;
								setCustomHeight(target.value);
								const height = parseInt(target.value);
								handleConfigChange(
									"height",
									isNaN(height)
										? imageToImageStore.config.manual_placement?.original_image_size?.[1]!
										: height,
								);
								if (customWidth) {
									resetOtherConfigs(t("customSize"));
								}
							}}
							type="number"
							customEndAdornmentText="px"
							height="40px"
						/>
					</InputLayout>
				</Box>

				{renderActions()}
			</Box>
		</Config>
	);

	const renderMediumDestinationConfig = () => (
		<Config
			title={t("mediumDestination")}
			checked={selectedConfig === t("mediumDestination")}
			onClick={() => {
				setSelectedConfig(t("mediumDestination"));
				resetOtherConfigs(t("mediumDestination"));
				handlePlatformSelection(selectedPlatformDimensions);
			}}
			isActive={selectedConfig == t("mediumDestination")}
			setIsActive={setSelectedConfig}
			titleOnly
		>
			<Box className={styles.configContent}>
				<BriaDropDown
					value={selectedPlatform}
					onChange={(value) => {
						handleMediumDestinationChange(value);
						setSelectedConfig(t("mediumDestination"));
						resetOtherConfigs(t("mediumDestination"));
					}}
					placeholder={t("dropDownPlaceholder")}
					items={platformsList}
					className={styles.dropDown}
					width="-webkit-fill-available"
				/>
				{selectedPlatform && (
					<Box className={styles.platformItems}>
						{cropConfig.map((platform: Platform) =>
							platform.get?.(selectedPlatform)?.map((item: ResponsePlatformDestination) => {
								const isSelected =
									item.name === selectedPlatformDimensions?.name &&
									item.width === selectedPlatformDimensions?.width &&
									item.height === selectedPlatformDimensions.height;
								return (
									<BriaButton
										key={`${selectedPlatform}-${item.name}`}
										onClick={() => {
											setSelectedConfig(t("mediumDestination"));
											resetOtherConfigs(t("mediumDestination"));
											handlePlatformSelection(item);
										}}
										className={clsx(styles.platformItem, { [styles.selected]: isSelected })}
									>
										<Box
											className={styles.platformOption}
											sx={{
												width: item.resize.width ?? 0,
												height: item.resize.height ?? 0,
											}}
										/>
										<Box>
											<Typography className={styles.label}>{item.name}</Typography>
											<Typography className={styles.label}>
												{item.width} x {item.height}
											</Typography>
										</Box>
									</BriaButton>
								);
							}),
						)}
					</Box>
				)}
				{renderActions()}
			</Box>
		</Config>
	);

	const renderIncreaseResolutionConfig = () => {
		const resolutionsList = IMAGES_RESOLUTIONS_LIST.filter((item) => item.key !== "X1");

		const handleIncreaseResolution = async (resolution: string) => {
			const resFactor = parseInt(resolution.replace("X", ""));
			if (selectedImage) {
				//
				imageToImageStore.handleConfigChange("size", {
					increase_resolution: {
						value: [resolution],
						selected: true,
					},
				});
				try {
					await imageToImageStore.increaseImageResolution(selectedImage, resFactor);
					logIncreaseResolutionEvent();
				} catch (e) {
					Sentry.captureMessage(`Error increasing image resolution: ${e}`);
					console.error(`Error increasing image resolution: ${e}`);
				}
			}
		};
		return (
			<Config
				title={t("increaseResolution")}
				checked={selectedConfig === t("increaseResolution")}
				titleOnly
				tooltip={t("increaseResolutionTooltip")}
				isActive
				className={styles.increaseResolutionConfig}
				apiDocLink={LinkConstants.BRIA_API_INCREASE_RESOLUTION}
				apiLinkPosition="bottom"
			>
				<Box className={clsx(styles.configContent, styles.increaseResolutionList)}>
					{resolutionsList?.map((resolution) => (
						<BriaButton
							key={resolution.key}
							disabled={
								!selectedImage ||
								imageToImageStore.manualPlacementStore.isResizingImages ||
								exceededResolutions?.includes(resolution.value) ||
								imageToImageStore.isGeneratingImages ||
								(hasTextToImageLoadingImages as boolean)
							}
							buttonType="secondarySmall"
							onClick={() => handleIncreaseResolution(resolution.value)}
							size="small"
						>
							{resolution.value}
						</BriaButton>
					))}
				</Box>
			</Config>
		);
	};

	const renderActions = (keep_aspect_ratio_value_in_config: boolean = false) => {
		return (
			<Box className={styles.actionsContainer}>
				<BriaButton
					className={styles.actionBtn}
					buttonType="secondarySmall"
					disabled={
						!selectedImage ||
						imageToImageStore.manualPlacementStore.isResizingImages ||
						imageToImageStore.isGeneratingImages ||
						(hasTextToImageLoadingImages as boolean)
					}
					onClick={handleCropImage}
					size="small"
				>
					{t("crop")}
				</BriaButton>
				<BriaButton
					className={styles.actionBtn}
					buttonType="primary"
					disabled={
						!selectedImage ||
						!hasNoTransparentArea() ||
						imageToImageStore.manualPlacementStore.isResizingImages ||
						imageToImageStore.isGeneratingImages ||
						(hasTextToImageLoadingImages as boolean)
					}
					onClick={() => {
						handleExpandBackground(keep_aspect_ratio_value_in_config);
					}}
					size="small"
				>
					{t("expandImage")}
				</BriaButton>
			</Box>
		);
	};

	return (
		<Box>
			<Box className={styles.manualPlacementConfigContainer}>
				{renderAspectRatioConfig()}
				{renderCustomSizeConfig()}
				{renderMediumDestinationConfig()}
				{hasOrganization() &&
					playgroundStore.selectedConfig !== APPS.AUTOMOTIVE &&
					renderIncreaseResolutionConfig()}
			</Box>
		</Box>
	);
};

export default observer(ManualPlacementConfig);
